import { ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import CogIcon from '@/assets/icons/cog.svg';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Typography } from '@/components/ui/typography';
import { useIndeedPanelParams } from '@/hooks/use-indeed-panel-params';
import { useIndeedSettings } from '@/hooks/use-indeed-settings';
import { useSourceFreemium } from '@/hooks/use-source-freemium';
import { useSourceIndeedContract } from '@/hooks/use-source-indeed-contract';
import { reportBoundaryError } from '@/utils/sentry';

import { StatusPill } from '../../channels/integrated/status-pill';

interface IndeedRowProps {
  children: (row: ReactNode) => ReactNode;
  disabled?: boolean;
}

export function IndeedRow(props: IndeedRowProps) {
  const enabled = useSourceIndeedContract();
  const freemium = useSourceFreemium();
  const fallback = props.children(null);

  if (!enabled || freemium) return fallback;

  return (
    <ErrorBoundary
      onError={reportBoundaryError('settings.indeed-contract-row')}
      fallback={fallback}
    >
      <Suspense fallback={fallback}>
        <ConnectedIndeedRow {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function ConnectedIndeedRow({ children, disabled }: IndeedRowProps) {
  const { data: settings } = useIndeedSettings();
  const { open } = useIndeedPanelParams();

  if (!settings.indeed_authorized) {
    return children(null);
  }

  return children(
    <>
      <div
        className="grid items-center gap-x-4 px-4 py-3"
        style={{ gridTemplateColumns: '1fr 1fr' }}
        data-testid="indeed-contract-row"
      >
        <div className="flex min-w-0 flex-col gap-2">
          <ChannelIcon
            channelSlug="indeed"
            channelName="Indeed"
            variant="large"
          />
          {settings.connected_by && (
            <Typography
              variant="small1"
              className="!text-fx-neutral-500 break-words"
            >
              <FormattedMessage
                defaultMessage="Added by {createdBy}"
                description="The user who connected the job board account"
                values={{ createdBy: settings.connected_by }}
              />
            </Typography>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <StatusPill isConnected />
          <Button
            variant="ghost"
            type="button"
            disabled={disabled}
            onClick={() => open()}
          >
            <span
              aria-hidden="true"
              className="bg-fx-brand-500 h-4 w-4"
              style={{ maskImage: `url("${CogIcon}")`, maskSize: 'contain' }}
            />
            <FormattedMessage
              defaultMessage="Configure"
              description="Action to change the connected job board account settings"
            />
          </Button>
        </div>
      </div>
      <div className="bg-fx-neutral-200 h-px w-full" />
    </>,
  );
}
